import React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { UxContext } from '../context/UxContext';
import { useGlobalStore } from '../store/globalStore/context/globalContext';
import { usePageStore } from '../store/pageStore/context/pageContext';
import i18n from '../utils/i18n';
import lediumApi from '../utils/lediumApi';

interface UseLediumPageOptions<T extends Record<string, unknown>> {
    postUpdate?: (data: T) => T;
    saveCondition?: () => boolean;
    saveDataMap: (saveFormData?: T | null) => Record<string, unknown>;
}

function useLediumPage<T extends Record<string, unknown>>(
    options: UseLediumPageOptions<T>
): { onSave: (saveFormData?: T | null) => void; mainMenu: () => void } {
    const { postUpdate, saveCondition, saveDataMap } = options;
    const { handleSnackbar } = React.useContext(UxContext)!;
    const {
        globalState: { userData }
    } = useGlobalStore();
    const {
        pageState: { formData, pageConfig },
        pageActions
    } = usePageStore();
    const { pageType, itemId } = useParams<{ pageType?: string; itemId?: string }>();
    const history = useHistory();

    const mainMenu = () => {
        history.push(pageConfig.url as string);
    };

    React.useEffect(() => {
        if (!pageConfig?.fetchEndPoint) {
            return;
        }

        switch (pageType) {
            case 'view':
                pageActions.api.loadItem<T>(itemId as string, postUpdate, { readOnly: true });
                break;
            case 'edit':
                pageActions.api.loadItem<T>(itemId as string, postUpdate, userData.admin ? {} : { readOnly: true });
                break;
            case 'new':
                pageActions.formData.overrideFormData({ ...pageConfig.initialData, loaded: true });
                break;
            default:
                mainMenu();
        }
    }, [pageType, itemId, pageConfig]);

    function onSave(saveFormData: T | null = null) {
        if (!saveFormData) {
            pageActions.formData.updateFormItem({ isSubmitted: true });
        }

        if (!saveCondition || (saveCondition && saveCondition())) {
            pageActions.formData.setFormSubmitting(true);
            const saveData = saveDataMap(saveFormData);
            const promise = formData.id
                ? lediumApi.patch(`${pageConfig.apiEndPoint}/${formData.id}`, saveData)
                : lediumApi.put(`${pageConfig.apiEndPoint}`, saveData);
            const infoKey = formData.id ? 'X_is_successfully_updated' : 'X_is_successfully_added';

            promise
                .then(() => {
                    handleSnackbar({
                        message: i18n(infoKey, pageConfig.itemName) as string
                    });

                    pageActions.formData.setFormSubmitting(false);
                    if (!saveFormData) {
                        mainMenu();
                    }
                })
                .catch(() => {
                    handleSnackbar({
                        message: i18n('unable_to_save_the_data') as string,
                        severity: 'error'
                    });
                    setTimeout(() => pageActions.formData.setFormSubmitting(false), 500);
                });
        }
    }

    return { onSave, mainMenu };
}

export default useLediumPage;
